// Collaboration Badges
//
// Reusable visual cues that tell a viewer how a pursuit relates to them:
//
//   <OriginBadge pursuit viewerRole />       — "Invited by client" / "Set up
//                                               by consultant" / hidden
//   <CollaboratorAvatars pursuit viewerOrgId
//                        max={3} />          — avatar stack of every org
//                                               with access (excluding the
//                                               viewer's own org), with a
//                                               "+N more" overflow chip
//
// Both components rely on the new `origin` + `collaborators` fields returned
// by `GET /api/pursuits` (see Part 1 of the Invitation Visibility sprint).
import { Building2, Users, Sparkles } from 'lucide-react';
import { cn } from '../../lib/utils';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '../ui/tooltip';

const ORG_PALETTE = [
  'bg-teal-100 text-teal-700 border-teal-200',
  'bg-indigo-100 text-indigo-700 border-indigo-200',
  'bg-amber-100 text-amber-700 border-amber-200',
  'bg-rose-100 text-rose-700 border-rose-200',
  'bg-emerald-100 text-emerald-700 border-emerald-200',
  'bg-purple-100 text-purple-700 border-purple-200',
];

/**
 * Deterministic palette pick so the same org always has the same avatar color.
 */
function paletteFor(orgId) {
  if (!orgId) return ORG_PALETTE[0];
  let h = 0;
  for (let i = 0; i < orgId.length; i++) h = (h * 31 + orgId.charCodeAt(i)) & 0xffffffff;
  return ORG_PALETTE[Math.abs(h) % ORG_PALETTE.length];
}

/**
 * "MKG Federal Consulting" → "MFC". "MKG Group LLC" → "MGL". Single word →
 * first 2 chars uppercase.
 */
function initialsFor(name) {
  if (!name) return '?';
  const parts = name
    .replace(/[^A-Za-z0-9 &-]/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 3);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return parts.map((p) => p[0]).join('').toUpperCase();
}

const isConsultantRole = (r) =>
  r === 'CONSULTANT' || r === 'CONSULTANT_ASSOCIATE';
const isContractorRole = (r) =>
  r === 'CONTRACTOR_ADMIN' || r === 'BID_MANAGER' || r === 'REVIEWER';

/**
 * OriginBadge — a single inline pill.
 * `pursuit.origin` values from the API:
 *   - "INVITED": the creator was in a different org than the viewer.
 *   - "SELF_CREATED": the creator was in the viewer's own org.
 *   - null: viewer wasn't passed (internal call); render nothing.
 *
 * We only render a badge for the more interesting "INVITED" case to keep the
 * card visually quiet when nothing's noteworthy.
 */
export function OriginBadge({ pursuit, viewerRole, size = 'sm' }) {
  if (!pursuit || pursuit.origin !== 'INVITED') return null;

  let label;
  let Icon;
  if (isConsultantRole(viewerRole)) {
    label = 'Invited by client';
    Icon = Sparkles;
  } else if (isContractorRole(viewerRole)) {
    label = 'Set up by consultant';
    Icon = Sparkles;
  } else {
    // SUPER_ADMIN or unknown — show generic
    label = 'External origin';
    Icon = Sparkles;
  }

  const sizing = size === 'lg'
    ? 'text-xs px-2.5 py-1'
    : 'text-[10px] px-2 py-0.5';

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full border font-medium',
        'bg-violet-50 text-violet-700 border-violet-200',
        sizing,
      )}
      data-testid={`origin-badge-${pursuit.id}`}
      data-origin={pursuit.origin}
    >
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
}

/**
 * CollaboratorAvatars — overlapping avatar stack of every collaborator org
 * EXCEPT the viewer's own org. Hover any avatar to see the full org name and
 * the date the link was established.
 */
export function CollaboratorAvatars({
  pursuit,
  viewerOrgId,
  max = 3,
  size = 'sm',
}) {
  const collaborators = pursuit?.collaborators || [];
  // Strip the viewer's own org — they already know they have access.
  const others = collaborators.filter((c) => c.org_id !== viewerOrgId);
  if (others.length === 0) return null;

  const visible = others.slice(0, max);
  const overflow = others.length - visible.length;

  const dim = size === 'lg' ? 'w-7 h-7 text-[11px]' : 'w-6 h-6 text-[10px]';

  return (
    <TooltipProvider delayDuration={200}>
      <div
        className="inline-flex items-center"
        data-testid={`collaborators-${pursuit.id}`}
      >
        <div className="flex -space-x-1.5">
          {visible.map((c) => (
            <Tooltip key={c.org_id}>
              <TooltipTrigger asChild>
                <div
                  className={cn(
                    'inline-flex items-center justify-center rounded-full border font-semibold ring-2 ring-white',
                    dim,
                    paletteFor(c.org_id),
                  )}
                  data-testid={`collab-${pursuit.id}-${c.org_id}`}
                >
                  {initialsFor(c.org_name)}
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <div className="text-xs">
                  <div className="font-semibold">{c.org_name}</div>
                  <div className="text-slate-300">
                    {c.role === 'OWNER' ? 'Pursuit owner' : 'Linked consultancy'}
                    {c.linked_at ? ` · linked ${new Date(c.linked_at).toLocaleDateString()}` : ''}
                  </div>
                </div>
              </TooltipContent>
            </Tooltip>
          ))}
        </div>
        {overflow > 0 && (
          <span
            className={cn(
              'ml-1 inline-flex items-center justify-center rounded-full border bg-slate-100 text-slate-600 border-slate-200 font-semibold',
              dim,
            )}
            title={`${overflow} more`}
          >
            +{overflow}
          </span>
        )}
      </div>
    </TooltipProvider>
  );
}

/**
 * CollaborationFootnote — used in the Intake / Go-No-Go header (Part 3 of
 * the sprint). Renders a single human-readable line summarizing the
 * collaboration arrangement.
 */
export function CollaborationFootnote({ pursuit, viewerRole, viewerOrgId }) {
  const collaborators = pursuit?.collaborators || [];
  const owner = collaborators.find((c) => c.role === 'OWNER');
  const consultants = collaborators.filter((c) => c.role === 'CONSULTANT');

  if (collaborators.length <= 1) {
    // No external collaboration to surface.
    return null;
  }

  // Decide which side of the relationship to lead with.
  const viewerIsOwner = owner && owner.org_id === viewerOrgId;

  if (viewerIsOwner) {
    // Contractor seeing their own pursuit shared with consultancies.
    if (consultants.length === 0) return null;
    const names = consultants.map((c) => c.org_name).join(', ');
    return (
      <div
        className="flex items-center gap-2 text-xs text-slate-600"
        data-testid="collab-footnote"
      >
        <Users className="w-3.5 h-3.5 text-slate-400" />
        <span>
          Shared with <span className="font-medium text-slate-800">{names}</span>
          {consultants[0]?.linked_at
            ? ` · linked ${new Date(consultants[0].linked_at).toLocaleDateString()}`
            : ''}
        </span>
      </div>
    );
  }

  if (owner && !viewerIsOwner) {
    // Consultant seeing a client's pursuit they were invited to.
    return (
      <div
        className="flex items-center gap-2 text-xs text-slate-600"
        data-testid="collab-footnote"
      >
        <Building2 className="w-3.5 h-3.5 text-slate-400" />
        <span>
          Pursuit owned by{' '}
          <span className="font-medium text-slate-800">{owner.org_name}</span>
          {' '}· you were invited to collaborate
        </span>
      </div>
    );
  }

  return null;
  // Suppress unused-import warning for default linting setups
  // (Building2 / Users are conditionally rendered above)
  // eslint-disable-next-line no-unreachable
  void viewerRole;
}
